import { useCallback, useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog';
import { toast } from '@/components/ui/sonner';
import { OpsHeading, OpsState, downloadBlob, errorText } from './OperationsUI';
import { DownloadCloud, Clock, Loader2, ShieldAlert, UploadCloud, PlayCircle, Save, FileJson } from 'lucide-react';

const WEEKDAYS = [['0', 'Lunes'], ['1', 'Martes'], ['2', 'Miércoles'], ['3', 'Jueves'], ['4', 'Viernes'], ['5', 'Sábado'], ['6', 'Domingo']];
const fmt = (iso) => { if (!iso) return '—'; try { return new Date(iso).toLocaleString('es', { dateStyle: 'medium', timeStyle: 'short' }); } catch { return iso; } };

export const PlatformBackups = () => {
  const [settings, setSettings] = useState(null);
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingSettings, setSavingSettings] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [running, setRunning] = useState(false);
  // Restore state
  const [restoreFile, setRestoreFile] = useState(null);
  const [restorePayload, setRestorePayload] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [confirmRestore, setConfirmRestore] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { const { data } = await api.get('/platform/backups'); setSettings(data.settings); setBackups(data.backups || []); }
    catch (e) { setError(errorText(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const { data } = await api.put('/platform/backups/settings', { enabled: settings.enabled, weekday: Number(settings.weekday), hour_utc: Number(settings.hour_utc), retention_count: Number(settings.retention_count) });
      setSettings(data); toast.success('Programación de copias actualizada');
    } catch (e) { toast.error(errorText(e)); } finally { setSavingSettings(false); }
  };

  const exportNow = async () => {
    setExporting(true);
    try { const { data } = await api.get('/platform/backup/export'); downloadBlob(JSON.stringify(data, null, 2), `bodegon53-backup-${Date.now()}.json`, 'application/json'); toast.success('Copia exportada'); }
    catch (e) { toast.error(errorText(e)); } finally { setExporting(false); }
  };

  const runNow = async () => {
    setRunning(true);
    try { const { data } = await api.post('/platform/backups/run-now'); toast.success(`Copia creada · ${data.total_documents} documentos`); load(); }
    catch (e) { toast.error(errorText(e)); } finally { setRunning(false); }
  };

  const downloadStored = async (bid, filename) => {
    try { const { data } = await api.get(`/platform/backups/${bid}`); downloadBlob(JSON.stringify(data, null, 2), filename || 'backup.json', 'application/json'); }
    catch (e) { toast.error(errorText(e)); }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0]; e.target.value = '';
    setPreview(null); setRestorePayload(null); setRestoreFile(null);
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result);
        if (!parsed || typeof parsed.collections !== 'object') { toast.error('El archivo no tiene el formato de copia esperado'); return; }
        setRestoreFile(f.name); setRestorePayload(parsed);
      } catch { toast.error('No se pudo leer el archivo JSON'); }
    };
    reader.readAsText(f);
  };

  const runPreview = async () => {
    if (!restorePayload) return;
    setPreviewing(true); setPreview(null);
    try { const { data } = await api.post('/platform/backup/preview', { collections: restorePayload.collections, mode: 'upsert' }); setPreview(data); }
    catch (e) { toast.error(errorText(e)); } finally { setPreviewing(false); }
  };

  const runRestore = async () => {
    if (!restorePayload) return;
    setRestoring(true);
    try {
      const { data } = await api.post('/platform/backup/restore', { collections: restorePayload.collections, mode: 'upsert' });
      const totals = Object.values(data.restored || {}).reduce((a, r) => ({ up: a.up + r.upserted, mod: a.mod + r.modified }), { up: 0, mod: 0 });
      toast.success(`Restauración aplicada · ${totals.up} creados, ${totals.mod} actualizados`);
      setPreview(null); setRestorePayload(null); setRestoreFile(null); load();
    } catch (e) { toast.error(errorText(e)); } finally { setRestoring(false); setConfirmRestore(false); }
  };

  return (
    <div data-testid="platform-backups-page">
      <OpsHeading title="Copias de seguridad y restauración" description="Exporta el estado de la base de datos, programa copias semanales y previsualiza qué cambiaría una restauración antes de aplicarla.">
        <Button variant="outline" data-testid="backup-export-now" disabled={exporting} onClick={exportNow}>{exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <DownloadCloud className="h-4 w-4" />} Exportar y descargar</Button>
        <Button className="seller-primary" data-testid="backup-run-now" disabled={running} onClick={runNow}>{running ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />} Crear copia ahora</Button>
      </OpsHeading>

      <Alert className="mb-5" data-testid="backup-disclaimer"><ShieldAlert className="h-4 w-4" /><AlertDescription>Las copias programadas se almacenan dentro de la misma base de datos como instantáneas con fecha. Para recuperación ante desastres descarga y guarda las copias fuera del sistema.</AlertDescription></Alert>

      <OpsState loading={loading} error={error} reload={load}>
        {settings && (
          <section className="seller-panel mb-6" data-testid="backup-schedule-panel">
            <div className="seller-section-row"><h2 className="flex items-center gap-2"><Clock className="h-5 w-5" /> Programación semanal</h2></div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mt-3">
              <div className="flex items-center justify-between rounded-lg border border-border p-3 sm:col-span-2 lg:col-span-1">
                <Label htmlFor="backup-enabled">Activar copias semanales</Label>
                <Switch id="backup-enabled" data-testid="backup-enabled" checked={!!settings.enabled} onCheckedChange={(v) => setSettings({ ...settings, enabled: v })} />
              </div>
              <div className="space-y-1.5">
                <Label>Día de la semana</Label>
                <Select value={String(settings.weekday)} onValueChange={(v) => setSettings({ ...settings, weekday: v })}>
                  <SelectTrigger data-testid="backup-weekday"><SelectValue /></SelectTrigger>
                  <SelectContent>{WEEKDAYS.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`backup-weekday-${v}`}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="backup-hour">Hora (UTC)</Label>
                <Input id="backup-hour" type="number" min={0} max={23} data-testid="backup-hour" value={settings.hour_utc} onChange={(e) => setSettings({ ...settings, hour_utc: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="backup-retention">Copias a conservar</Label>
                <Input id="backup-retention" type="number" min={1} max={52} data-testid="backup-retention" value={settings.retention_count} onChange={(e) => setSettings({ ...settings, retention_count: e.target.value })} />
              </div>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-4">
              <Button className="seller-primary" data-testid="backup-save-settings" disabled={savingSettings} onClick={saveSettings}>{savingSettings ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Guardar programación</Button>
              <span className="text-sm text-muted-foreground" data-testid="backup-next-run">Próxima ejecución: <strong>{settings.enabled ? fmt(settings.next_run_at) : 'Desactivada'}</strong></span>
              <span className="text-sm text-muted-foreground" data-testid="backup-last-run">Última: {fmt(settings.last_run_at)}</span>
            </div>
          </section>
        )}

        {/* Restore with preview */}
        <section className="seller-panel mb-6" data-testid="backup-restore-panel">
          <div className="seller-section-row"><h2 className="flex items-center gap-2"><UploadCloud className="h-5 w-5" /> Restaurar desde archivo</h2></div>
          <p className="seller-help mt-1">Sube un archivo de copia (.json). Primero verás un resumen de los cambios; la restauración solo se aplica al confirmar. La restauración fusiona por identificador (no borra registros existentes que no estén en la copia).</p>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <input ref={fileRef} type="file" accept="application/json,.json" className="hidden" data-testid="backup-restore-file" onChange={onFile} />
            <Button variant="outline" data-testid="backup-restore-choose" onClick={() => fileRef.current?.click()}><FileJson className="h-4 w-4" /> {restoreFile || 'Elegir archivo de copia'}</Button>
            <Button variant="outline" data-testid="backup-restore-preview" disabled={!restorePayload || previewing} onClick={runPreview}>{previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Previsualizar cambios</Button>
            <Button className="seller-primary" data-testid="backup-restore-apply" disabled={!preview || restoring} onClick={() => setConfirmRestore(true)}>{restoring ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Restaurar</Button>
          </div>
          {preview && (
            <div className="mt-4" data-testid="backup-preview">
              <div className="flex flex-wrap gap-2 mb-3">
                <Badge variant="outline" data-testid="backup-preview-create">Se crearían: {preview.totals.create}</Badge>
                <Badge variant="outline" data-testid="backup-preview-update">Se actualizarían: {preview.totals.update}</Badge>
                <Badge variant="outline" data-testid="backup-preview-skipped">Omitidos (sin id): {preview.totals.skipped}</Badge>
              </div>
              <div className="seller-table-wrap border border-border rounded-lg">
                <Table><TableHeader><TableRow><TableHead>Colección</TableHead><TableHead>En el archivo</TableHead><TableHead>Nuevos</TableHead><TableHead>Actualizar</TableHead><TableHead>Omitidos</TableHead><TableHead>Actuales</TableHead></TableRow></TableHeader>
                  <TableBody>{preview.summary.map((r) => <TableRow key={r.collection} data-testid={`backup-preview-row-${r.collection}`}><TableCell className="font-medium">{r.collection}</TableCell><TableCell>{r.incoming}</TableCell><TableCell className="text-brand-sage font-semibold">{r.would_create}</TableCell><TableCell className="text-brand-orange font-semibold">{r.would_update}</TableCell><TableCell>{r.skipped_no_id}</TableCell><TableCell className="text-muted-foreground">{r.current_documents}</TableCell></TableRow>)}</TableBody>
                </Table>
              </div>
              {preview.excluded_collections?.length > 0 && <p className="text-xs text-muted-foreground mt-2" data-testid="backup-preview-excluded">Colecciones excluidas por seguridad: {preview.excluded_collections.join(', ')}</p>}
            </div>
          )}
        </section>

        {/* Stored backups */}
        <section className="seller-panel" data-testid="backup-list-panel">
          <div className="seller-section-row"><h2>Copias almacenadas</h2><Badge variant="outline">{backups.length}</Badge></div>
          {backups.length ? (
            <div className="seller-table-wrap mt-3">
              <Table><TableHeader><TableRow><TableHead>Fecha</TableHead><TableHead>Tipo</TableHead><TableHead>Documentos</TableHead><TableHead>Archivo</TableHead><TableHead>Acciones</TableHead></TableRow></TableHeader>
                <TableBody>{backups.map((b) => <TableRow key={b.id} data-testid={`backup-row-${b.id}`}>
                  <TableCell>{fmt(b.created_at)}</TableCell>
                  <TableCell><Badge variant="outline" data-testid={`backup-kind-${b.id}`}>{b.kind === 'SCHEDULED' ? 'Programada' : 'Manual'}</Badge></TableCell>
                  <TableCell>{b.total_documents}</TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-48 truncate">{b.filename}</TableCell>
                  <TableCell><Button variant="outline" size="sm" data-testid={`backup-download-${b.id}`} onClick={() => downloadStored(b.id, b.filename)}><DownloadCloud className="h-4 w-4" /> Descargar</Button></TableCell>
                </TableRow>)}</TableBody>
              </Table>
            </div>
          ) : <p className="seller-help mt-3" data-testid="backup-list-empty">Aún no hay copias almacenadas. Crea una copia ahora o activa la programación semanal.</p>}
        </section>
      </OpsState>

      <AlertDialog open={confirmRestore} onOpenChange={(v) => { if (!restoring) setConfirmRestore(v); }}>
        <AlertDialogContent className="seller-dialog" data-testid="backup-restore-confirmation">
          <AlertDialogHeader><AlertDialogTitle>Confirmar restauración</AlertDialogTitle><AlertDialogDescription>Se aplicarán {preview?.totals.create || 0} creaciones y {preview?.totals.update || 0} actualizaciones sobre los datos actuales. Esta acción fusiona por identificador y quedará registrada en la auditoría.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel disabled={restoring} data-testid="backup-restore-cancel">Cancelar</AlertDialogCancel><AlertDialogAction className="seller-primary" disabled={restoring} data-testid="backup-restore-confirm" onClick={(e) => { e.preventDefault(); runRestore(); }}>{restoring ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Confirmar restauración</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
